import type { TextareaHTMLAttributes, InputHTMLAttributes } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface TextInputProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  error?: string;
  characterCount?: number;
  maxCharacters?: number;
  register: UseFormRegisterReturn;
  className?: string;
  disabled?: boolean;
}

/**
 * 文本输入组件
 * 支持单行和多行输入，显示字符计数和错误提示
 */
export function TextInput({
  label,
  placeholder,
  required = false,
  multiline = false,
  error,
  characterCount,
  maxCharacters,
  register,
  className,
  disabled = false,
}: TextInputProps) {
  const inputId = register.name;

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor={inputId} className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {characterCount !== undefined && maxCharacters !== undefined && (
          <span className="text-xs text-muted-foreground">
            {characterCount}/{maxCharacters}
          </span>
        )}
      </div>
      {multiline ? (
        <Textarea
          {...(register as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          id={inputId}
          placeholder={placeholder}
          disabled={disabled}
          className={error ? 'border-destructive focus-visible:ring-destructive' : ''}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
      ) : (
        <Input
          {...(register as InputHTMLAttributes<HTMLInputElement>)}
          id={inputId}
          placeholder={placeholder}
          disabled={disabled}
          className={error ? 'border-destructive focus-visible:ring-destructive' : ''}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
      )}
      {error && (
        <p id={`${inputId}-error`} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
